import React from "react";
import { t } from "@auto/services";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

import * as api from "../api";
import {
  createChildPayload,
  entityTitle,
  getNumericField,
  labelOf,
  normalizeTags,
} from "../model";
import {
  closeDialog,
  openDialog,
  setDialogError,
  setDialogSaving,
} from "../state";
import { useObservable } from "../hooks";
import { canCreateGeographyEntity, canUpdateGeographyEntity } from "@src/security/permissions";

import InlineTags from "@auto/widgets/InlineTags";

import VOTING_CENTER_TAG_TYPES from "../metadata/votingcentertagtypes.mjs";

import styles from "../../ElectoralGeographyPage.module.styl";

const DEFAULT_VOTING_CENTER_CAPACITY = 3500;

const getInitialCapacity = (initialValue) => {
  const value = getNumericField(initialValue, "capacity", "capacity");
  return value === "" ? String(DEFAULT_VOTING_CENTER_CAPACITY) : String(value);
};

const getSavedCapacity = (value) => {
  return String(value || "").trim()
    ? Number(value)
    : DEFAULT_VOTING_CENTER_CAPACITY;
};

const GeographyDialog = ({ gui, state, setState, events }) => {
  const [code, setCode] = React.useState("");
  const [name, setName] = React.useState("");
  const [address, setAddress] = React.useState("");
  const [capacity, setCapacity] = React.useState(String(DEFAULT_VOTING_CENTER_CAPACITY));
  const [supervisorPassword, setSupervisorPassword] = React.useState("");
  const [tags, setTags] = React.useState({});
  const [arrondissementGid, setArrondissementGid] = React.useState("");
  const [constituencyGid, setConstituencyGid] = React.useState("");

  const dialog = state.dialog;

  const handleCreateRequested = React.useCallback(({ entity, parent = null, parentType = "" }) => {
    if (!canCreateGeographyEntity(gui, entity)) {
      return;
    }

    openDialog(setState, "create", entity, parent, parentType, null);
  }, [gui, setState]);

  const handleEditRequested = React.useCallback(({ entity, item }) => {
    if (!canUpdateGeographyEntity(gui, entity)) {
      return;
    }

    openDialog(setState, "edit", entity, null, "", item);
  }, [gui, setState]);

  useObservable(events.createRequested, handleCreateRequested);
  useObservable(events.editRequested, handleEditRequested);

  React.useEffect(() => {
    if (!dialog.open) {
      return;
    }

    const initialValue = dialog.initialValue;
    const parent = dialog.parent;
    const parentType = dialog.parentType;

    setCode(initialValue?.code || "");
    setName(initialValue?.name || "");
    setAddress(initialValue?.address || "");
    setCapacity(getInitialCapacity(initialValue));
    setSupervisorPassword(initialValue?.supervisorPassword || initialValue?.supervisor_password || "");
    setTags(normalizeTags(initialValue?.tags));

    setArrondissementGid(
      initialValue?.arrondissementGid ||
      initialValue?.arrondissement_gid ||
      (parentType === "arrondissement" ? parent?.gid : "") ||
      ""
    );

    setConstituencyGid(
      initialValue?.constituencyGid ||
      initialValue?.constituency_gid ||
      (parentType === "constituency" ? parent?.gid : "") ||
      ""
    );
  }, [dialog.open, dialog.initialValue, dialog.parent, dialog.parentType]);

  const isVotingCenter = dialog.entity === "voting-center";
  const isCommune = dialog.entity === "commune";
  const title = `${dialog.mode === "edit" ? t("common.edit") : t("common.add")} ${entityTitle(dialog.entity).toLowerCase()}`;
  const parentLabel = dialog.parent ? labelOf(dialog.parent) : "";
  const canSave = dialog.open && (
    dialog.mode === "edit"
      ? canUpdateGeographyEntity(gui, dialog.entity)
      : canCreateGeographyEntity(gui, dialog.entity)
  );

  const save = async () => {
    if (!canSave) {
      return;
    }

    setDialogSaving(setState, true);
    setDialogError(setState, "");

    const values = {
      ...(dialog.mode === "edit" && dialog.initialValue?.gid
        ? { gid: dialog.initialValue.gid }
        : {}),
      code: code.trim() || null,
      name: name.trim(),
      ...(isVotingCenter
        ? {
            address: address.trim() || null,
            capacity: getSavedCapacity(capacity),
            supervisorPassword: supervisorPassword.trim() || null,
            tags: normalizeTags(tags),
          }
        : {}),
      ...(isCommune
        ? {
            arrondissementGid: Number(arrondissementGid),
            constituencyGid: Number(constituencyGid),
          }
        : {}),
    };

    const localValues = {
      ...values,
      ...(dialog.mode === "edit" && dialog.initialValue?.gid
        ? { gid: dialog.initialValue.gid }
        : {}),
    };

    try {
      if (dialog.mode === "edit") {
        await api.updateEntity(dialog.entity, values);
      } else {
        await api.createEntity(
          dialog.entity,
          createChildPayload(dialog.entity, dialog.parent, dialog.parentType, values)
        );
      }

      closeDialog(setState);
      events.entitySaved.publish({ entity: dialog.entity, item: localValues });
    } catch (err) {
      setDialogError(setState, err?.message || String(err));
    } finally {
      setDialogSaving(setState, false);
    }
  };

  return (
    <Dialog
      open={dialog.open}
      onClose={dialog.saving ? undefined : () => closeDialog(setState)}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <div className={styles.dialogBody}>
          {parentLabel ? (
            <div className={styles.contextNote}>
              {t("common.parent")}: <strong>{parentLabel}</strong>
            </div>
          ) : null}

          {dialog.error ? (
            <div className={styles.dialogError}>
              {dialog.error}
            </div>
          ) : null}

          <TextField
            label={t("common.code")}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            fullWidth
            size="small"
          />

          <TextField
            label={t("common.name")}
            value={name}
            onChange={(event) => setName(event.target.value)}
            fullWidth
            size="small"
          />

          {isCommune && dialog.parentType === "arrondissement" ? (
            <TextField
              select
              label={t("geo.constituency")}
              value={constituencyGid}
              onChange={(event) => setConstituencyGid(event.target.value)}
              fullWidth
              size="small"
            >
              {state.data.constituencies.map((constituency) => (
                <MenuItem key={constituency.gid} value={constituency.gid}>
                  {labelOf(constituency)}
                </MenuItem>
              ))}
            </TextField>
          ) : null}

          {isCommune && dialog.parentType === "constituency" ? (
            <TextField
              select
              label={t("geo.arrondissement")}
              value={arrondissementGid}
              onChange={(event) => setArrondissementGid(event.target.value)}
              fullWidth
              size="small"
            >
              {state.data.arrondissements.map((arrondissement) => (
                <MenuItem key={arrondissement.gid} value={arrondissement.gid}>
                  {labelOf(arrondissement)}
                </MenuItem>
              ))}
            </TextField>
          ) : null}

          {isVotingCenter ? (
            <>
              <TextField
                label={t("geo.capacity")}
                type="number"
                value={capacity}
                onChange={(event) => setCapacity(event.target.value)}
                fullWidth
                size="small"
              />

              <TextField
                label={t("geo.supervisorPassword")}
                value={supervisorPassword}
                onChange={(event) => setSupervisorPassword(event.target.value)}
                fullWidth
                size="small"
              />

              <TextField
                label={t("common.address")}
                value={address}
                onChange={(event) => setAddress(event.target.value)}
                fullWidth
                size="small"
                multiline
                minRows={3}
              />

              <div className={styles.tagsBlock}>
                <InlineTags
                  tags={tags}
                  tagTypes={VOTING_CENTER_TAG_TYPES}
                  onChange={async (nextTags) => setTags(normalizeTags(nextTags))}
                />
              </div>
            </>
          ) : null}
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => closeDialog(setState)} disabled={dialog.saving}>
          {t("common.cancel")}
        </Button>

        <Button
          variant="contained"
          disabled={
            dialog.saving ||
            !canSave ||
            !name.trim() ||
            (isCommune && !String(arrondissementGid || "").trim()) ||
            (isCommune && !String(constituencyGid || "").trim())
          }
          onClick={save}
        >
          {t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GeographyDialog;